/** Exact conversions between decimal strings and integer base units (lamports, token base units). No floats. */

export const SOL_DECIMALS = 9;
export const LAMPORTS_PER_SOL = 1_000_000_000n;
export const TOKEN_DECIMALS = 6;

/** "1.5" -> 1_500_000_000n for decimals 9. Returns null for anything that is not a plain non-negative decimal. Extra decimals are truncated. */
export function parseUnits(input: string, decimals: number): bigint | null {
  const s = input.trim().replace(/,/g, '');
  if (!/^\d*\.?\d*$/.test(s) || s === '' || s === '.') return null;
  const [int = '', frac = ''] = s.split('.');
  const f = (frac + '0'.repeat(decimals)).slice(0, decimals);
  try {
    return BigInt(int || '0') * 10n ** BigInt(decimals) + BigInt(f || '0');
  } catch {
    return null;
  }
}

/** 1_500_000_000n -> "1.5" for decimals 9 (exact; trailing zeros trimmed; at most `maxDecimals` digits, truncated). */
export function formatUnits(value: bigint, decimals: number, maxDecimals = decimals): string {
  const neg = value < 0n;
  const v = neg ? -value : value;
  const base = 10n ** BigInt(decimals);
  const int = v / base;
  let frac = (v % base).toString().padStart(decimals, '0').slice(0, Math.max(0, maxDecimals));
  frac = frac.replace(/0+$/, '');
  return `${neg ? '-' : ''}${int.toString()}${frac ? '.' + frac : ''}`;
}

export const parseSol = (s: string) => parseUnits(s, SOL_DECIMALS);
export const lamportsToSol = (l: bigint, maxDecimals = 9) => formatUnits(l, SOL_DECIMALS, maxDecimals);
export const parseTokens = (s: string, decimals = TOKEN_DECIMALS) => parseUnits(s, decimals);
export const baseToTokens = (b: bigint, decimals = TOKEN_DECIMALS, maxDecimals = decimals) => formatUnits(b, decimals, maxDecimals);

/** Float view of an integer amount (display only). */
export function unitsToNumber(value: bigint | string, decimals: number): number {
  const v = typeof value === 'string' ? BigInt(value || '0') : value;
  const base = 10n ** BigInt(decimals);
  return Number(v / base) + Number(v % base) / Number(base);
}

export const lamportsToNumber = (v: bigint | string) => unitsToNumber(v, SOL_DECIMALS);
export const baseToNumber = (v: bigint | string, decimals = TOKEN_DECIMALS) => unitsToNumber(v, decimals);

/** Safe BigInt from an API decimal string (empty or malformed -> 0n). */
export function big(v: string | number | bigint | undefined | null): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.trunc(v)) : 0n;
  if (!v) return 0n;
  try {
    return BigInt(v);
  } catch {
    return 0n;
  }
}
